import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore, hospitals } from '@/stores/authStore'
import { usePatientStore } from '@/stores/patientStore'
import './PatientList.css'

export default function PatientList() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPatients, setSelectedPatients] = useState<string[]>([])
  const [filterHospital, setFilterHospital] = useState<string>('all')
  const { user } = useAuthStore()
  const { patients } = usePatientStore()

  // 権限に基づくフィルタリング
  const isAdminOrOffice = user?.role === 'admin' || user?.role === 'office'
  const userHospitalId = user?.hospitalId

  // フィルタリング済み患者データ
  const filteredPatients = useMemo(() => {
    return patients.filter((patient) => {
      // 医師の場合は自院データのみ表示
      if (!isAdminOrOffice && userHospitalId) {
        if (patient.primaryHospitalId !== userHospitalId && 
            patient.transplantHospitalId !== userHospitalId) {
          return false
        }
      }

      // 病院フィルター
      if (filterHospital !== 'all' && 
          patient.primaryHospitalId !== filterHospital &&
          patient.transplantHospitalId !== filterHospital) {
        return false
      }

      // 検索クエリ
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        return (
          patient.name.toLowerCase().includes(query) ||
          patient.researchId.toLowerCase().includes(query) ||
          patient.diseaseName.includes(query) ||
          (patient.primaryDoctorName || '').toLowerCase().includes(query)
        )
      }

      return true
    })
  }, [patients, searchQuery, filterHospital, isAdminOrOffice, userHospitalId])

  // 医師の場合、フィルターで選択可能な病院を自院のみに制限
  const availableHospitals = isAdminOrOffice 
    ? hospitals 
    : hospitals.filter(h => h.id === userHospitalId)

  const toggleSelectAll = () => {
    if (selectedPatients.length === filteredPatients.length) {
      setSelectedPatients([])
    } else {
      setSelectedPatients(filteredPatients.map((p) => p.id))
    }
  }

  const toggleSelect = (id: string) => {
    setSelectedPatients((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    )
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <h1 className="aip-main__header-title">患者カルテ</h1>
          {!isAdminOrOffice && user?.hospitalName && (
            <span className="aip-main__header-subtitle">
              <span className="material-symbols-outlined">local_hospital</span>
              {user.hospitalName}
            </span>
          )}
          <div className="aip-main__header-buttons">
            <Link
              to="/patients/create"
              className="aip-button aip-button--solid-fill"
            >
              <span className="material-symbols-outlined">add</span>
              新規作成
            </Link>
          </div>
        </div>
      </header>

      <div className="aip-main__content">
        <div className="patient-list-container">
          {/* ツールバー */}
          <div className="aip-table__header">
            <div className="aip-table__header-area">
              <div className="aip-search-bar">
                <span className="material-symbols-outlined aip-search-bar__icon">
                  search
                </span>
                <input
                  className="aip-search-bar__input"
                  type="text"
                  placeholder="患者名、研究会ID、病名、医師名で検索"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {isAdminOrOffice && (
                <select
                  className="aip-select"
                  value={filterHospital}
                  onChange={(e) => setFilterHospital(e.target.value)}
                >
                  <option value="all">すべての医療機関</option>
                  {availableHospitals.map((hospital) => (
                    <option key={hospital.id} value={hospital.id}>
                      {hospital.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="aip-table__header-area">
              <span className="result-count">
                {filteredPatients.length}件
              </span>
            </div>
          </div>

          {/* テーブル */}
          <div className="aip-table">
            <table>
              <thead>
                <tr>
                  <th className="aip-table__checkbox">
                    <input
                      type="checkbox"
                      checked={selectedPatients.length === filteredPatients.length && filteredPatients.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>研究会ID</th>
                  <th>患者ID</th>
                  <th>患者名</th>
                  <th>性別</th>
                  <th>年齢</th>
                  <th>病名</th>
                  {isAdminOrOffice && <th>医療機関</th>}
                  <th>担当医師</th>
                  <th>移植回数</th>
                  <th>評価</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredPatients.map((patient) => (
                  <tr key={patient.id}>
                    <td className="aip-table__checkbox">
                      <input
                        type="checkbox"
                        checked={selectedPatients.includes(patient.id)}
                        onChange={() => toggleSelect(patient.id)}
                      />
                    </td>
                    <td>{patient.researchId}</td>
                    <td>{patient.patientId}</td>
                    <td>{patient.name}</td>
                    <td>{patient.gender}</td>
                    <td>{patient.age}</td>
                    <td>
                      <span className={`disease-tag disease-tag--${getDiseaseClass(patient.diseaseName)}`}>
                        {patient.diseaseName}
                      </span>
                    </td>
                    {isAdminOrOffice && <td>{patient.primaryHospitalName}</td>}
                    <td>{patient.primaryDoctorName}</td>
                    <td>{patient.transplantCount}</td>
                    <td>
                      {patient.evaluation && (
                        <span className={`evaluation-tag evaluation-tag--${getEvaluationClass(patient.evaluation)}`}>
                          {patient.evaluation}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="aip-table__actions">
                        <Link
                          to={`/patients/${patient.id}`}
                          className="aip-button aip-button--icon aip-button--small"
                          title="詳細"
                        >
                          <span className="material-symbols-outlined">visibility</span>
                        </Link>
                        <Link
                          to={`/patients/${patient.id}/edit`}
                          className="aip-button aip-button--icon aip-button--small"
                          title="編集"
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ページネーション */}
          <div className="aip-pagination">
            <button className="aip-pagination__button" disabled>
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <span className="aip-pagination__info">
              1 / {Math.ceil(filteredPatients.length / 10)} ページ
            </span>
            <button className="aip-pagination__button">
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// 病名に基づくCSSクラス
function getDiseaseClass(diseaseName: string): string {
  const classMap: { [key: string]: string } = {
    '潰瘍性大腸炎': 'uc',
    '自閉スペクトラム症': 'asd',
    '過敏性腸症候群': 'ibs',
    'アトピー・アレルギー': 'atopy',
    'アトピー性皮膚炎': 'atopy',
    '便秘症': 'constipation',
    'うつ病': 'depression',
    'クローン病': 'crohn',
  }
  return classMap[diseaseName] || 'other'
}

// 評価に基づくCSSクラス
function getEvaluationClass(evaluation: string): string {
  if (evaluation.includes('著効') || evaluation.includes('治癒')) return 'excellent'
  if (evaluation.includes('中程度')) return 'moderate'
  if (evaluation.includes('軽度')) return 'mild'
  return 'none'
}
